import React, { useRef, useState, useEffect } from 'react';
import { Box, Paper, Slider, Stack } from "@mui/material";
import { styled } from '@mui/system';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeMuteIcon from '@mui/icons-material/VolumeMute';
import calm from '../assets/calm.mp3';  
import bird from '../assets/bird.mp3';
import { themes } from '../lib/theme';

const CustomPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  padding: theme.spacing(2),
  width: '20%',
  '&:hover .slider-container': {
    display: 'flex',
  },
}));

const PSlider = styled(Slider)(({ theme, showThumb }) => ({
  height: 2,
  transition: 'all 0.3s ease',
  '& .MuiSlider-thumb': {
    width: '13px',
    height: '13px',
    display: showThumb ? 'block' : 'none',
    visibility: showThumb ? 'visible' : 'hidden',
    transition: 'all 0.3s ease',
  },
  '&:hover': {
    cursor: 'pointer',
    '& .MuiSlider-thumb': {
      display: 'block',
      visibility: 'visible',
    },
  },
}));

const playlist = [bird, calm];

export default function MusicPlayer({ theme }) {
  const audioPlayer = useRef();
  const [index, setIndex] = useState(0);
  const [volume, setVolume] = useState(50);
  const [mute, setMute] = useState(true);
  const [hover, setHover] = useState(false);

  const currentSong = playlist[index];

  useEffect(() => {
    const audioElement = audioPlayer.current;
    if (audioElement) {
      audioElement.volume = volume / 100;
      audioElement.muted = mute;
      audioElement.play().catch(err => console.log('Error playing audio:', err));
    }
  }, [volume, mute, currentSong]);

  useEffect(() => {
    const audioElement = audioPlayer.current;
    if (audioElement) {
      audioElement.addEventListener('ended', handleSongEnded);
      return () => {
        audioElement.removeEventListener('ended', handleSongEnded);
      };
    }
  }, [currentSong]);

  const handleSongEnded = () => {
    setIndex((prevIndex) => (prevIndex + 1) % playlist.length);
  };

  const handleSliderChange = (e, value) => {
    setVolume(value);
  };

  const handleSliderChangeCommitted = () => {
    if (mute) {
      setMute(false);
    }
  };

  function VolumeBtns() {
    return mute
      ? <VolumeOffIcon 
          sx={{ 
            color: themes[theme].buttonColor, 
            cursor: 'pointer', 
            transition: 'color 0.3s ease, transform 0.3s ease', 
            '&:hover': { 
              color: themes[theme].buttonColor, 
              opacity: 0.8, 
              transform: 'scale(1.1)' 
            } 
          }} 
          onClick={() => setMute(!mute)} 
          onMouseEnter={() => setHover(true)} 
          onMouseLeave={() => setHover(false)} 
        />
      : volume <= 20 
        ? <VolumeMuteIcon 
            sx={{ 
              color: themes[theme].buttonColor, 
              cursor: 'pointer', 
              transition: 'color 0.3s ease, transform 0.3s ease', 
              '&:hover': { 
                color: themes[theme].buttonColor, 
                opacity: 0.8, 
                transform: 'scale(1.1)' 
              } 
            }} 
            onClick={() => setMute(!mute)} 
            onMouseEnter={() => setHover(true)} 
            onMouseLeave={() => setHover(false)} 
          />
        : volume <= 75 
          ? <VolumeDownIcon 
              sx={{ 
                color: themes[theme].buttonColor, 
                cursor: 'pointer', 
                transition: 'color 0.3s ease, transform 0.3s ease', 
                '&:hover': { 
                  color: themes[theme].buttonColor, 
                  opacity: 0.8, 
                  transform: 'scale(1.1)' 
                } 
              }} 
              onClick={() => setMute(!mute)} 
              onMouseEnter={() => setHover(true)} 
              onMouseLeave={() => setHover(false)} 
            />
          : <VolumeUpIcon 
              sx={{ 
                color: themes[theme].buttonColor, 
                cursor: 'pointer', 
                transition: 'color 0.3s ease, transform 0.3s ease', 
                '&:hover': { 
                  color: themes[theme].buttonColor, 
                  opacity: 0.8, 
                  transform: 'scale(1.1)' 
                } 
              }} 
              onClick={() => setMute(!mute)} 
              onMouseEnter={() => setHover(true)} 
              onMouseLeave={() => setHover(false)} 
            />;
  }

  return (
    <div>
      <audio src={currentSong} ref={audioPlayer} autoPlay preload='true' />
      <CustomPaper
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Stack direction='row' spacing={1} sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', alignItems: 'center' }}>
            <VolumeBtns />
            <Box className="slider-container" sx={{ display: 'none', minWidth: '100px', marginLeft: '10px' }}>
              <PSlider
                min={0}
                max={100}
                value={volume}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderChangeCommitted}
                showThumb={hover || !mute}
                sx={{ color: themes[theme].buttonColor }}
              />
            </Box>
          </Stack>
        </Box>
      </CustomPaper>
    </div>
  );
}
